import React from "react";
import { useTranslation } from "react-i18next";
import { OverviewAreaChart } from "@/components/pages/analytics/front-desk/components/overview/OverviewAreaChart";
import { OverviewKpiCards } from "@/components/pages/analytics/front-desk/components/overview/OverviewKpiCards";
import { useAnalyticsPage } from "@/components/pages/analytics/front-desk/context/AnalyticsPageContext";
import { useAdminOverviewHotels } from "../../hooks/useAdminOverviewHotels";
import { useAdminOverviewTableExport } from "../../hooks/useAdminOverviewTableExport";
import { AdminOverviewHotelsTable } from "./AdminOverviewHotelsTable";

export const AdminOverviewTab = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { dateRange } = useAnalyticsPage();
  const {
    tenantId,
    tenantName,
    rows,
    networkMetrics,
    kpiMetrics,
    chartData,
    isLoading,
    isError,
    isReady,
  } = useAdminOverviewHotels(dateRange);

  useAdminOverviewTableExport({
    dateRange,
    tenantName,
    rows,
    networkMetrics,
    canExport: Boolean(tenantId) && isReady && !isLoading && !isError,
  });

  if (!tenantId) {
    return (
      <p className="text-zinc-500">
        {t("analytics.admin.noTenant", "Tenant is not selected.")}
      </p>
    );
  }

  if (!isReady) {
    return (
      <p className="text-zinc-500">
        {t(
          "analytics.admin.overview.selectDateRange",
          "Select a date range to view hotel overview.",
        )}
      </p>
    );
  }

  if (isError) {
    return (
      <p className="text-zinc-500">
        {t(
          "analytics.admin.overview.loadError",
          "Failed to load hotel overview data.",
        )}
      </p>
    );
  }

  return (
    <section className="space-y-6">
      <OverviewKpiCards metrics={kpiMetrics} isLoading={isLoading} />
      <AdminOverviewHotelsTable rows={rows} loading={isLoading} />
      <OverviewAreaChart
        data={chartData}
        isLoading={isLoading}
        isError={isError}
      />
    </section>
  );
};
